import { useCompanyTests } from "@/hooks/useCompanyTests";
import { CompanyTestCard } from "./CompanyTestCard";
import { InProgressTestsSection } from "@/components/dashboard/InProgressTestsSection";
import { Skeleton } from "@/components/ui/skeleton";
import type { CompanyPortalConfig } from "@/config/companies";

interface CompanyTestsListProps {
  config: CompanyPortalConfig;
}

export function CompanyTestsList({ config }: CompanyTestsListProps) {
  const { data, isLoading } = useCompanyTests(config.organizationId);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-64 rounded-lg" />
        ))}
      </div>
    );
  }

  const { publicTests = [], invitedTests = [], inProgressTests = [] } = data || {};

  return (
    <div className="space-y-14">
      {/* In Progress Tests Section */}
      {inProgressTests.length > 0 && (
        <InProgressTestsSection tests={inProgressTests} />
      )}

      {/* Invited Tests Section */}
      {config.features?.showInvitedTests && invitedTests.length > 0 && (
        <section>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-foreground">
            Your Invited Tests
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {invitedTests.map((test) => (
              <CompanyTestCard 
                key={test.id} 
                test={test} 
                accentColor={config.branding.primaryColor}
              />
            ))}
          </div>
        </section>
      )}

      {/* Public Tests Section */}
      {config.features?.showPublicTests && (
        <section>
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-foreground">
            Available Public Tests
          </h2>
          {publicTests.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {publicTests.map((test) => (
                <CompanyTestCard 
                  key={test.id} 
                  test={test} 
                  accentColor={config.branding.primaryColor}
                />
              ))}
            </div>
          ) : invitedTests.length > 0 ? (
            <div className="text-center py-16 px-4">
              <p className="text-lg text-muted-foreground">No public tests available at the moment</p>
              <p className="text-sm text-muted-foreground/80 mt-2">Check back later for new opportunities</p>
            </div>
          ) : null}
        </section>
      )}

      {/* Empty State */}
      {invitedTests.length === 0 && publicTests.length === 0 && (
        <div className="text-center py-20 px-4">
          <p className="text-xl font-medium text-foreground mb-2">No tests available at the moment</p>
          <p className="text-muted-foreground">Check back later for new opportunities</p>
        </div>
      )}
    </div>
  );
}
